import type { ReactNode } from 'react'
import { Banner, Button, Card, SectionLabel } from '../../design/components'
import styles from '../images/ImageGallery.module.css'
import { jobStatusLine, type GenerationJob } from './types'
import type { GenerationJobState } from './useGenerationJob'

export interface GenerationCardProps {
  label: string
  /** What pressing the button will do, in a sentence. */
  what: string
  /** The button's own words — "Generate a portrait", "Draw the battle map". */
  action: string
  job: GenerationJobState
  /** Hidden entirely when false: a control that only ever 403s is worse than no control. */
  canGenerate: boolean
  /** Extra controls — a prompt field, a reference picker. */
  children?: ReactNode
  /** Rendered when a finished job carried text rather than an image. */
  renderText?: (text: string) => ReactNode
}

/**
 * The one progress surface for every generation.
 *
 * All five kinds behave identically from here — `202`, then a status line, then either a
 * refetch or a reason — so they share a card rather than each growing their own. What
 * differs is only the words and what the finished job carried.
 *
 * The states it distinguishes matter:
 *
 * - **Running.** A live status line from the job's own `status`, with Cancel. Cancelling a
 *   job that already wrote its result answers `409`, which is not surfaced: cancellation is
 *   not a rollback and saying "too late" after the fact helps nobody.
 * - **Finished.** For the image kinds the result is a *pointer* to something already saved
 *   and linked, so the card says so and the caller has already refetched.
 * - **Swept.** The job record is gone. That is not an error — see `interpretJobLookupFailure`
 *   — and reads as "finished", because a successful job persists its output before
 *   reporting success.
 * - **Failed.** The job's own `error`, which the API promises is a human-readable reason
 *   and never a stack trace.
 */
export function GenerationCard({
  label,
  what,
  action,
  job,
  canGenerate,
  children,
  renderText,
}: GenerationCardProps) {
  if (!canGenerate) return null

  const finishedText = textOf(job.job)
  const settled = !job.isRunning && (job.job !== null || job.assumedFinished)

  return (
    <Card variant="magic" label={label} hint={job.isRunning ? 'Working' : undefined}>
      {job.failure !== null && <Banner tone="error">{job.failure}</Banner>}

      {!job.isRunning && !settled && (
        <>
          <p className={styles.note}>{what}</p>
          {children}
          <div className={styles.actions}>
            <Button onClick={job.run}>{action}</Button>
          </div>
        </>
      )}

      {job.isRunning && (
        <>
          {/* `role="status"` so a screen reader is told the generation finished without
              having to poll the page — the whole point of the line is that it changes. */}
          <p className={styles.rowText} role="status">
            {jobStatusLine(job.job, job.assumedFinished)}
          </p>
          <div className={styles.actions}>
            <Button variant="secondary" onClick={job.cancel}>
              Cancel
            </Button>
          </div>
        </>
      )}

      {settled && (
        <>
          <p className={styles.rowText} role="status">
            {jobStatusLine(job.job, job.assumedFinished)}
          </p>

          {finishedText !== null && renderText !== undefined && (
            <div className={styles.field}>
              <SectionLabel tone="muted" micro>
                Result
              </SectionLabel>
              {renderText(finishedText)}
            </div>
          )}

          <div className={styles.actions}>
            <Button variant="secondary" onClick={job.dismiss}>
              Done
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}

/**
 * The text a finished job carried, if any.
 *
 * Only `CharacterDescription` sets it: it returns prose for the owner to read and save
 * deliberately rather than writing it to the bio behind their back. Every other kind
 * returns a pointer to a saved image, which is why the rest of the card talks about
 * refetching instead.
 */
function textOf(job: GenerationJob | null): string | null {
  const text = job?.result?.text
  return text === undefined || text === null || text === '' ? null : text
}
